import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, MessageCircle, ArrowRight, CheckCircle, Clock, Star, Building } from "lucide-react";

const CITY_DATA = {
  mohali: {
    name: "Mohali",
    title: "Best Psychiatrist in Mohali",
    seo_title: "Best Psychiatrist in Mohali | Dr. Ambrish Singal | 28+ Years Experience",
    description: "Dr. Ambrish Singal is one of the most experienced and trusted psychiatrists in Mohali, providing comprehensive mental health care from his clinic at 704 HT Towers, Sector 79, Sahibzada Ajit Singh Nagar.",
    clinics: [
      {
        name: "Dr. Ambrish Singal Clinic",
        address: "Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar, Punjab 140308",
        features: ["Outpatient psychiatric consultations", "Drug de-addiction programmes", "Sexual health consultations", "Prior appointment required"],
      },
    ],
    areas: ["Sector 79", "Sector 69", "Phase 1-11", "IT City", "Aerocity", "Kharar", "Sunny Enclave", "Gillco Valley", "Zirakpur border"],
  },
  chandigarh: {
    name: "Chandigarh",
    title: "Best Psychiatrist in Chandigarh",
    seo_title: "Best Psychiatrist in Chandigarh | Dr. Ambrish Singal",
    description: "Serving Chandigarh residents with comprehensive psychiatric care. Dr. Ambrish Singal's clinic in Sector 79, SAS Nagar is conveniently located just minutes from Chandigarh.",
    clinics: [
      {
        name: "Dr. Ambrish Singal Clinic (nearest to Chandigarh)",
        address: "Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar — 15-20 minutes from most Chandigarh sectors",
        features: ["Easy access from Chandigarh", "Ample parking", "Comprehensive psychiatric services", "Prior appointment ensures no waiting"],
      },
    ],
    areas: ["Sector 1-50", "Manimajra", "Panchkula border", "IT Park", "Industrial Area", "Elante Mall area", "Railway Station area", "Airport Road"],
  },
  panchkula: {
    name: "Panchkula",
    title: "Best Psychiatrist in Panchkula",
    seo_title: "Best Psychiatrist in Panchkula | Dr. Ambrish Singal",
    description: "Residents of Panchkula and surrounding areas can access expert psychiatric care from Dr. Ambrish Singal at his clinic in Sector 79, SAS Nagar. We serve patients from across the Tricity region including Panchkula.",
    clinics: [
      {
        name: "Dr. Ambrish Singal Clinic",
        address: "Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar — approximately 30-40 minutes from Panchkula",
        features: ["Worth the drive for expert psychiatric care", "Prior appointment ensures no waiting", "Comprehensive evaluation and treatment"],
      },
    ],
    areas: ["Sector 1-20", "Industrial Area", "Kalka Road", "Ramgarh", "Barwala", "Morni Hills", "Chandimandir"],
  },
};

const KEYWORDS_BY_CITY = {
  mohali: ["Psychiatrist in Mohali", "De-addiction centre Mohali", "Sexologist in Mohali", "Anxiety treatment Mohali", "Depression doctor Mohali"],
  chandigarh: ["Psychiatrist in Chandigarh", "Best psychiatrist Chandigarh", "Depression treatment Chandigarh", "Anxiety doctor Chandigarh", "De-addiction Chandigarh"],
  panchkula: ["Psychiatrist near Panchkula", "Mental health doctor Panchkula", "De-addiction near Panchkula", "Anxiety treatment Panchkula"],
};

export default function LocationPage() {
  const { city } = useParams();
  const data = CITY_DATA[city?.toLowerCase()];

  if (!data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Location not found.</p>
        <Link to="/"><Button variant="outline">Back to Home</Button></Link>
      </div>
    );
  }

  return (
    <div>
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-6 h-6 text-primary" />
            <span className="text-sm text-muted-foreground uppercase tracking-wider">{data.name}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="location-heading">
            {data.title}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {data.description}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href="tel:+919915096633" data-testid="location-call-btn">
              <Button className="gap-2 rounded-full"><Phone className="w-4 h-4" /> Call Now</Button>
            </a>
            <a href="https://wa.me/919815333660" target="_blank" rel="noopener noreferrer" data-testid="location-whatsapp-btn">
              <Button variant="outline" className="gap-2 rounded-full"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
            </a>
            <Link to="/contact" data-testid="location-book-btn">
              <Button variant="secondary" className="gap-2 rounded-full">Book Appointment <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Clinics */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Clinic Locations</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {data.clinics.map((clinic) => (
              <Card key={clinic.name} className="border">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-heading font-semibold">{clinic.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{clinic.address}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {clinic.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-primary shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Areas We Serve in {data.name}</h2>
          <div className="flex flex-wrap gap-2">
            {data.areas.map((area) => (
              <span key={area} className="px-4 py-2 rounded-full bg-card border text-sm">{area}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Available */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Services Available for {data.name} Patients</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Depression & Mood Disorders",
              "Anxiety & Panic Attacks",
              "OCD Treatment",
              "Alcohol & Drug De-addiction",
              "Sexual Health (Certified Sexologist)",
              "Child & Adolescent Psychiatry",
              "Dementia & Geriatric Care",
              "Schizophrenia & Psychosis",
              "Sleep Disorders",
              "ADHD Assessment & Treatment",
              "PTSD & Trauma Therapy",
              "Medication Management",
            ].map((s) => (
              <div key={s} className="flex items-center gap-2 p-3 rounded-lg bg-card border">
                <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Why {data.name} Patients Choose Dr. Ambrish Singal</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Star, title: "28+ Years Experience", desc: "One of the most experienced psychiatrists in the Tricity" },
              { icon: Building, title: "Modern Facilities", desc: "State-of-the-art clinic with inpatient care available" },
              { icon: Clock, title: "Timely Appointments", desc: "Prior appointment system ensures minimal waiting time" },
              { icon: CheckCircle, title: "Evidence-Based Care", desc: "Latest treatment protocols and medication management" },
            ].map((item) => (
              <Card key={item.title} className="border">
                <CardContent className="p-5 text-center">
                  <item.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-heading font-semibold text-sm">{item.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
            Looking for a Psychiatrist in {data.name}?
          </h2>
          <p className="text-base opacity-90 mb-8">
            Book your confidential consultation with Dr. Ambrish Singal today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919915096633">
              <Button size="lg" variant="secondary" className="gap-2 rounded-full">
                <Phone className="w-5 h-5" /> Call +91-99150 96633
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="gap-2 rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                Book Online <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
